/*
  CLOSING ONHAND SIGNS YOU OUT, 18 September 2026, on the owner's word.

  He closed Safari with OnHand open, opened it again, typed the address, and
  was straight into his home page without signing in: *"That should never
  happen."* It happened because on 7 September the session moved from the
  tab's own storage (emptied when the tab closes) to the browser's lasting
  storage, to cure a sign-in fault; staying signed in came with that move and
  nobody chose it. It also turned out that opening OnHand counts as "somebody
  did something" before the two hour idle clock is looked at, so even hours
  after the browser was closed the clock never caught it.

  **The rule, his answers of 18 September 2026**: when every OnHand tab and
  window is closed, or the browser is quit, the next visit asks for a sign-in.
  A second tab opened while one is still open carries on signed in.

  **How.** When OnHand opens:

  - if this same tab closed a moment ago, it is a reload: carry on;
  - otherwise it calls out to any other open OnHand tab and waits a moment
    (`ASK_MS`) for one to answer. An answer means OnHand is open elsewhere:
    carry on. Silence means nobody has it open, so the saved sign-in is
    deleted and the sign-in screen shows.

  **Asking, not keeping a list.** The first version kept a list of open tabs in
  storage, each crossing itself off as it closed. Driven in Chrome it failed
  the owner's exact case: quitting a browser does not reliably let its tabs
  cross themselves off, so after a restart the list still named a tab that no
  longer existed and he stayed signed in. Only a tab that is really running can
  answer a question.

  **The Supabase client waits for the answer** before it reads the saved
  session, through `sessionStorageGate` below, which `src/lib/supabase.js`
  hands it as its storage (the same keys in the same `localStorage`, so no
  saved session changes shape). `src/index.js` imports this file first so the
  question goes out as early as possible.

  Limits, written down rather than discovered later:
  - A browser that cannot pass messages between tabs (`BroadcastChannel`) is
    treated as having no other tab, so it signs in every time. Every current
    browser has it.
  - A tab the browser has frozen in the background cannot answer, so opening a
    new tab beside one signs both out. That is the safe side to be wrong on.
  - The saved sign-in is deleted from this browser only.
*/

export const LEFT_KEY = 'onhand:tabLeftAt';
export const VERDICT_KEY = 'onhand:tabVerdict';
export const RELOAD_WINDOW_MS = 10 * 1000;
export const ASK_MS = 400;
const CHANNEL = 'onhand-tabs';
const SUPABASE_KEY = /^sb-.+-auth-token(-code-verifier|-user)?$/;

export function isReload(now, leftAt) {
  return !!leftAt && now - leftAt >= 0 && now - leftAt < RELOAD_WINDOW_MS;
}

export function forgetSignIn(local) {
  try {
    const doomed = [];
    for (let i = 0; i < local.length; i++) { const k = local.key(i); if (k && SUPABASE_KEY.test(k)) doomed.push(k); }
    doomed.forEach(k => local.removeItem(k));
  } catch (e) { /* storage refused: nothing was saved to delete */ }
}

/*
  Starts the check and returns `{ ready }`, a promise of what was decided:
  'reload', 'another-tab', 'fresh', or 'no-storage'. After deciding, this tab
  answers any other tab that asks, for as long as it is open.
*/
export function startTabSession(win, askMs = ASK_MS) {
  let local, session;
  try { local = win.localStorage; session = win.sessionStorage; local.getItem('x'); } catch (e) { return { ready: Promise.resolve('no-storage') }; }
  const now = Date.now();
  let leftAt = 0;
  try { leftAt = Number(session.getItem(LEFT_KEY)) || 0; session.removeItem(LEFT_KEY); } catch (e) { leftAt = 0; }
  const Channel = win.BroadcastChannel;
  const channel = Channel ? new Channel(CHANNEL) : null;
  const id = Math.random().toString(36).slice(2) + now.toString(36);
  let settled = false;

  const ready = new Promise(resolve => {
    const settle = verdict => {
      if (settled) return;
      settled = true;
      if (verdict === 'fresh') forgetSignIn(local);
      try { session.setItem(VERDICT_KEY, verdict); } catch (e) { /* only a note for tracing */ }
      resolve(verdict);
    };
    if (channel) {
      channel.onmessage = e => {
        const m = e.data || {};
        // Answer only once this tab has settled: tabs opening at the same moment
        // (a restored browser) must not vouch for each other.
        if (m.type === 'anyone-here' && m.from !== id && settled) channel.postMessage({ type: 'here', to: m.from });
        if (m.type === 'here' && m.to === id) settle('another-tab');
      };
    }
    if (isReload(now, leftAt)) { settle('reload'); return; }
    if (!channel) { settle('fresh'); return; }
    channel.postMessage({ type: 'anyone-here', from: id });
    win.setTimeout(() => settle('fresh'), askMs);
  });

  const onLeave = () => { try { session.setItem(LEFT_KEY, String(Date.now())); } catch (e) { /* storage refused */ } };
  win.addEventListener('pagehide', onLeave);
  return {
    ready, id,
    stop: () => { win.removeEventListener('pagehide', onLeave); if (channel) channel.close(); },
  };
}

export const TAB_SESSION = (typeof window !== 'undefined' && process.env.NODE_ENV !== 'test')
  ? startTabSession(window)
  : null;

/*
  The storage the Supabase client is given: `localStorage`, exactly as before,
  except that nothing is read or written until this tab has decided whether
  the saved sign-in stands.
*/
const gate = () => (TAB_SESSION ? TAB_SESSION.ready : Promise.resolve());
export const sessionStorageGate = {
  getItem: async key => { await gate(); try { return window.localStorage.getItem(key); } catch (e) { return null; } },
  setItem: async (key, value) => { await gate(); try { window.localStorage.setItem(key, value); } catch (e) { /* storage refused */ } },
  removeItem: async key => { await gate(); try { window.localStorage.removeItem(key); } catch (e) { /* storage refused */ } },
};
